/**
 * hookchain.mjs — where you attach a rule decides whether it is a guard or an obituary.
 *
 * FOUNDER, 2026-08-15: "i want claude code chapters to deep dive" → chapter 9's instrument.
 *
 * ── WHY THIS CHAPTER EARNS AN INSTRUMENT AND MOST DO NOT ──────────────────────────────────────
 * §5 teaches an ASYMMETRY: "PreToolUse is prevention. PostToolUse is evidence… A log is not a
 * guard." Prose can state that. It cannot make it happen to you.
 *
 * An instrument can. The learner attaches one rule to one event, the action runs down the chain,
 * and the trace stops where the rule stopped it — or does not. Placing a rule on PostToolUse and
 * watching a `git clean` complete, with a perfect record of what was destroyed, teaches the
 * sentence in a way that reading it does not.
 *
 * ── THERE IS NO SINGLE RIGHT ANSWER, AND THAT IS THE POINT ────────────────────────────────────
 * A lab where PreToolUse is always correct would teach "block everything", which is chapter 4's
 * `manual` posture wearing a hook's clothes — slow, and it trains the reflex approval §2 warns
 * about. On this machine the commit-scope guard sits on PostToolUse deliberately, because a commit
 * can be undone and what that incident lacked was not the power to refuse but the fact of being
 * noticed. §5's added paragraph names the axis: RECOVERABILITY, not severity.
 *
 * So the same placement is right for one action and catastrophic for another, and the learner finds
 * that out by placing it.
 *
 * ── EVERY ACTION AND EVERY HOOK BELOW IS REAL, AND ON THIS MACHINE ────────────────────────────
 * Same law as chapter 16's claim lab. Invented examples would teach routing invented actions. Each
 * action here is one this estate has actually taken or actually banned; each `real` placement is a
 * hook wired in ~/.claude/settings.json right now, named by the script that runs; each quote is
 * that script's own words. gate-hookchain.mjs re-reads the live settings file and fails if any of
 * it has drifted — the placements are re-derived, never remembered.
 *
 * ── ONE FACT, ONE PLACE ───────────────────────────────────────────────────────────────────────
 * The six events and their matcher counts are NOT restated here. They are imported from ccfacts,
 * which is the same lesson that file learned the hard way when a gate count lived in two places and
 * the two disagreed. This module adds only what ccfacts does not know: which event can refuse, and
 * where each one sits in the order of a tool call.
 */

import { EXTENSIONS } from './ccfacts.mjs';

/* The section MOVED when chapter 9 was brought to depth — §5 became §8, and the heading changed
   from "The asymmetry that matters" to what it is now. A citation that still pointed at §5 would
   send a reader to the failure-swallowing section instead, which is a different argument. A
   citation nobody can follow is not a citation, so the gate checks this against the chapter's own
   headings rather than trusting the string. */
export const SOURCE = 'CLAUDECODE101 chapter 9 §8 "Where to attach it: recoverability, not severity"';

/** §1: "five of the six events observe, and one can refuse." */
export const REFUSES = 'PreToolUse';

/**
 * Where each event falls in the life of one tool call.
 *
 * `Notification` is deliberately absent, and its absence is a finding rather than an oversight. §4
 * says only that it fires "when it wants your attention" — which is not a fixed point in a tool
 * call. This course cannot establish whether it lands before or after a given action, so it does
 * not draw it on the line and does not pretend to know what a rule placed there would have done.
 * An instrument that guessed would be manufacturing exactly the fourth kind of claim that chapter
 * 16 teaches learners to fear, inside the academy that teaches it.
 */
const ORDER = Object.freeze({ SessionStart: 0, UserPromptSubmit: 1, PreToolUse: 2, PostToolUse: 3, Stop: 4 });

export const EVENTS = Object.freeze(EXTENSIONS.hooks.map((h) => Object.freeze({
  id: h.event,
  matchers: h.matchers,
  when: h.when,
  can_refuse: h.event === REFUSES,
  order: ORDER[h.event] ?? null,
  /* An event with no established position cannot be traced, and says so rather than being hidden. */
  untraceable: ORDER[h.event] === undefined,
})));

export const NOWHERE = 'nowhere';
export const eventOf = (id) => EVENTS.find((e) => e.id === id) || null;
export const traceable = () => EVENTS.filter((e) => !e.untraceable).sort((a, b) => a.order - b.order);

/**
 * ── THE ACTIONS ───────────────────────────────────────────────────────────────────────────────
 *
 * Real, every one. Four are things this estate refuses before they run; one is a thing it watches
 * afterwards on purpose; one is an incident that actually happened and went unnoticed because
 * nothing failed.
 *
 * `recoverable` is the axis §5 adds, and it is never a bare boolean on screen — the learner sees
 * WHY it can or cannot be taken back, because the reason is the transferable part. "Untracked files
 * are not in the object store" survives into tools this course has never heard of; "git clean is
 * bad" does not.
 */
export const ACTIONS = Object.freeze([
  {
    id: 'gitclean',
    tool: 'Bash',
    what: 'git clean -fd, on a tree where another session has uncommitted files',
    recoverable: false,
    recovery: 'Nothing downstream can restore them. Untracked files were never written to the object store, so there is no commit, no stash and no reflog to recover from — the bytes are gone.',
    real: [
      { event: 'PreToolUse', hook: 'node /root/.ankr/scripts/git-guard.mjs',
        job: 'refuses it outright, and names the rule so the refusal reads as a rule rather than a fault' },
    ],
    quote: "'git clean' is BANNED on the shared tree — it is the only git op that deletes UNTRACKED work (another session's uncommitted files), unrecoverably.",
    quote_from: '/root/.ankr/scripts/git-guard.mjs',
  },
  {
    id: 'commitscope',
    tool: 'Bash',
    what: 'a commit meant to hold 2 files that instead swept 595, because the message ate its own pathspec',
    recoverable: true,
    recovery: 'A commit can be undone. The history is in the object store and nothing was destroyed — the damage was that it went unnoticed, not that it was permanent.',
    real: [
      { event: 'PostToolUse', hook: 'node /root/.ankr/scripts/git-commit-scope-guard.mjs',
        job: 'reads what the commit actually contained and reports any file outside the pathspec you named' },
    ],
    /* The guard's own header, and the sharpest sentence in this lab: the failure mode of a
       recoverable action is silence, not damage. */
    quote: 'Nothing failed. Exit code 0. It was found only because someone happened to print `git show --stat` afterwards.',
    quote_from: '/root/.ankr/scripts/git-commit-scope-guard.mjs',
  },
  {
    id: 'hotfile',
    tool: 'Edit',
    what: 'editing a file another live session touched three minutes ago',
    recoverable: false,
    recovery: 'Not by the tool. Two sessions writing the same file do not produce a conflict to resolve — the second write wins silently, and the first session carries on believing its work is there.',
    /* THE CENTREPIECE. One script, three events, three different jobs — the chapter's whole
       argument existing as an artefact rather than as advice. */
    real: [
      { event: 'SessionStart', hook: 'node /root/.ankr/scripts/edit-heat-guard.mjs session-start',
        job: 'announces which other sessions are live, so the fact is in the window before anything is proposed' },
      { event: 'PreToolUse', hook: 'node /root/.ankr/scripts/edit-heat-guard.mjs pre',
        job: 'blocks the write if that file was edited within the window by another live session' },
      { event: 'PostToolUse', hook: 'node /root/.ankr/scripts/edit-heat-guard.mjs post',
        job: 'records the touch, which is what makes the next session\'s block possible at all' },
    ],
    quote: 'The coexistence doctrine\'s `git log -1` heat check cannot see UNCOMMITTED writers — this guard can.',
    quote_from: '/root/.ankr/scripts/edit-heat-guard.mjs',
  },
  {
    id: 'schema',
    tool: 'Bash',
    what: 'a schema change applied straight through psql, bypassing the tool that takes a snapshot first',
    recoverable: false,
    recovery: 'A dropped column takes its data with it. Recovery depends entirely on a backup existing beforehand — which is precisely what the guarded path exists to guarantee and the unguarded one does not.',
    real: [
      { event: 'PreToolUse', hook: 'node /root/.ankr/scripts/db-schema-guard.mjs',
        job: 'makes the snapshot-first tool the only door to a schema apply' },
    ],
    /* Exact, including the lower-case opening word. The first draft capitalised it and added a full
       stop the file does not have — two silent alterations, which is how the seven elided quotes
       this project has already shipped each began. */
    quote: 'it was a tool you had to remember… the second step — the psql — was completely unguarded',
    quote_from: '/root/.ankr/scripts/db-schema-guard.mjs',
  },
  {
    id: 'secret',
    tool: 'Read',
    what: 'reading a file that holds a live credential',
    /* The irreversibility here is DISCLOSURE, not deletion — and that is why the lab includes it.
       A learner who has sorted recoverability by "does it delete something" will place this one
       wrong, and find out why in one move. */
    recoverable: false,
    recovery: 'Nothing can un-send it. Once the contents are in the window they have already left the machine, and the only remedy left is rotation — which is a different, slower and costlier act than prevention.',
    real: [
      { event: 'PreToolUse', hook: 'bun run /root/aegis/src/cli/index.ts check-shield',
        job: 'inspects the target before the read happens, on Read, Write, Edit and Bash alike' },
    ],
    /* NOT A QUOTE, AND THE DISTINCTION IS THE COURSE'S OWN.
       The first draft of this entry put a sentence of mine in quotation marks and attributed it to
       settings.json — a claim I had counted, dressed as one I had read. That is precisely chapter
       16's fourth kind wearing the first kind's clothes, committed inside the academy that teaches
       it. It is a count, so it is labelled a count and the gate re-derives the number. */
    counted: 'check-shield is wired to 4 of the 15 PreToolUse matchers — Bash, Read, Write and Edit.',
    counted_by: 'count of PreToolUse matchers whose command names check-shield, in ~/.claude/settings.json',
  },
  {
    id: 'spawn',
    tool: 'Agent',
    what: 'spawning a subagent that will run unattended and spend budget',
    recoverable: true,
    recovery: 'It can be stopped. What it spent cannot be recovered, but the work it touched is ordinary work and the cost is bounded — which is why this one is checked rather than banned.',
    real: [
      { event: 'PreToolUse', hook: 'bun run /root/aegis/src/cli/index.ts check-spawn',
        job: 'checks the spawn against the budget before it starts, rather than reporting the bill afterwards' },
    ],
    counted: 'check-spawn is wired to the Agent matcher, and check-budget to every tool — 2 of the 15 PreToolUse matchers.',
    counted_by: 'count of PreToolUse matchers naming check-spawn or check-budget, in ~/.claude/settings.json',
  },
]);

export const actionOf = (id) => ACTIONS.find((a) => a.id === id) || null;

/**
 * ── THE CHAIN ─────────────────────────────────────────────────────────────────────────────────
 *
 * Run the action down the events in order, with the learner's rule attached at `placement`.
 * Returns the steps that actually occurred — the trace ENDS at a refusal, because that is what a
 * refusal means, and a trace that carried on past it would be teaching the opposite of the chapter.
 */
export function trace(actionId, placement) {
  const action = actionOf(actionId);
  if (!action) return [];
  const steps = [];
  let stopped = false;

  for (const ev of traceable()) {
    const mine = ev.id === placement;
    if (ev.id === 'PreToolUse') {
      steps.push({ event: ev.id, mine, fired: true,
        note: mine ? 'your rule runs here, and this is the one place a rule may refuse' : ev.when });
      if (mine) {
        steps.push({ event: null, refused: true, note: `the ${action.tool} call does not run` });
        stopped = true;
        break;
      }
      steps.push({ event: null, happened: true, note: `the ${action.tool} call runs — ${action.what}` });
      continue;
    }
    steps.push({ event: ev.id, mine, fired: true,
      note: mine
        ? (ev.id === 'PostToolUse' ? 'your rule runs here — it can record what happened, and nothing else'
          : ev.id === 'SessionStart' ? 'your rule runs here, before anything is proposed — it can put a fact in the window, not refuse an action'
          : ev.id === 'UserPromptSubmit' ? 'your rule runs here — it sees the request, and cannot stop the tool call the request leads to'
          : 'your rule runs here, after the work is over')
        : ev.when });
  }
  return { steps, stopped };
}

/**
 * ── WHAT THE PLACEMENT ACTUALLY BOUGHT ────────────────────────────────────────────────────────
 *
 * Four outcomes, and the reading of three of them depends on whether the action can be taken back.
 * Nothing here is a score and nothing is called right or wrong: `recorded` on a recoverable action
 * is the correct engineering answer and the lab says so plainly, because a lab that treated it as a
 * near-miss would be teaching learners to block work that should flow.
 */
export function outcome(actionId, placement) {
  const a = actionOf(actionId);
  if (!a) return null;
  const ev = eventOf(placement);

  if (placement === NOWHERE) {
    return { kind: 'unobserved', happened: true, recorded: false,
      headline: 'It happened, and nothing knows',
      reading: a.recoverable
        ? 'Recoverable, so the loss is bounded — but you will find out by noticing, and the incident this action is drawn from went unnoticed for exactly that reason.'
        : 'Unrecoverable and unobserved. There is no version of this you can reconstruct afterwards, because nothing was watching to reconstruct it from.' };
  }
  if (ev?.untraceable) {
    return { kind: 'unknown', happened: null, recorded: null,
      headline: 'This course cannot tell you',
      reading: `§4 says only that ${ev.id} fires "when it wants your attention". That is not a fixed point in a tool call, so where your rule would have run relative to this action is not established — and rather than guess, the honest answer is that it is not known.` };
  }
  if (ev?.can_refuse) {
    return { kind: 'prevented', happened: false, recorded: false,
      headline: 'The action did not happen',
      reading: a.recoverable
        ? 'It works — though notice what it costs. This action can be undone, so refusing it every time stops work that should have flowed. Prevention is not free; it is paid for in the ordinary cases that now need a human.'
        : 'The only placement that means anything here. ' + a.recovery };
  }
  if (placement === 'PostToolUse' || placement === 'Stop') {
    return { kind: 'recorded', happened: true, recorded: true,
      headline: a.recoverable ? 'It happened, you know, and you can take it back'
        : 'An exact account of something that cannot be undone',
      reading: a.recoverable
        ? 'This is the right placement, not a near-miss. The failure mode of a recoverable action is silence — being noticed is the whole requirement, and a rule here supplies it without slowing anything down.'
        : 'A log is not a guard. You will be able to reconstruct precisely how it happened, in detail, afterwards. ' + a.recovery };
  }
  return { kind: 'informed', happened: true, recorded: false,
    headline: 'The action proceeded — but what gets proposed may change',
    reading: `${placement} cannot refuse a tool call. What it can do is put a fact into the window that was not there before, which changes what is likely to be asked for in the first place. That is a real mechanism and a weak one: it influences, it does not decide, and this lab will not claim it prevented anything.` };
}

/* ── THE SENTENCES THE LAB MUST NOT LOSE ───────────────────────────────────────────────────── */
export const THE_AXIS = 'Which one you need first is decided by whether the action can be taken back — not by how alarming it sounds. Severity is a poor guide: it tempts you into blocking recoverable work that should flow, and it waves through the quiet permanent things, a credential read into a window or a directory removed.';
export const A_LOG_IS_NOT_A_GUARD = 'A team with excellent logging and no PreToolUse rules will be able to reconstruct exactly how the accident happened, in detail, afterwards.';
export const THE_REVERSE = 'A guard with no log means you prevented something and have no record of how often, or whether the rule is still firing. A rule nobody can see working is a rule that quietly rots.';
export const WHY_REAL = 'Every action here is one this estate has actually taken or actually banned, and every placement shown is a hook wired on this machine right now — named by the script that runs it, in that script\'s own words. A gate re-reads the live settings file and fails if any of it has drifted.';
export const NOT_ALWAYS_PRE = 'PreToolUse is not the answer to everything. On this machine the commit-scope guard sits on PostToolUse on purpose: a commit can be undone, and what that incident lacked was not the power to refuse but the fact of being noticed.';
